function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="font-heading text-deep-blue text-xl font-semibold border-b border-[var(--gold)]/30 pb-2">
        {title}
      </h2>
      <div className="space-y-3 text-earth-brown/85 leading-relaxed">{children}</div>
    </div>
  )
}

function Bullets({ items }) {
  return (
    <ul className="list-disc list-outside ml-5 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

const SUB_PROCESSORS = [
  {
    name: 'Supabase',
    purpose: 'Database, authentication, edge functions',
    data: 'Account email, profile, chat history, all app data',
  },
  {
    name: 'Stripe',
    purpose: 'Payment processing for Premium subscriptions',
    data: 'Card details (handled by Stripe — we never see or store card numbers)',
  },
  {
    name: 'MailerLite',
    purpose: 'Email marketing — Newsletter and lifecycle groups (welcome, premium)',
    data: 'Email address, display name (optional), group membership',
  },
  {
    name: 'Resend',
    purpose: 'Transactional email (contact-form notifications, system emails)',
    data: 'Email address, message content',
  },
  {
    name: 'PostHog (US Cloud)',
    purpose: 'Product analytics + session replay on non-sensitive routes',
    data: 'Anonymous session data + your Supabase user ID after login. Loads only when you accept cookies. Recording is disabled on /chat, /prayer, /bible, /ministry-map, and /learn.',
  },
  {
    name: 'Sentry',
    purpose: 'Error tracking',
    data: 'Anonymized user ID, error stack traces. Email and message bodies are scrubbed before send.',
  },
  {
    name: 'Vercel',
    purpose: 'Website hosting + edge network',
    data: 'IP address at request time (used for routing, not stored long-term)',
  },
  {
    name: 'Anthropic (Claude API)',
    purpose: 'AI generation for chat, study tools, sermon prep',
    data: 'Your chat messages and any context you attach (preferences, passage, profile signals). Anthropic processes the request and returns a response; per their API terms, prompts are not used to train their models.',
  },
  {
    name: 'Meta Pixel',
    purpose: 'Conversion measurement for Meta ads',
    data: 'Fires only on signup completion and Premium subscribe events. No chat content.',
  },
  {
    name: 'API.Bible',
    purpose: 'Backend Scripture fetches (NKJV, NASB 2020, NLT)',
    data: 'No user data — server-to-server passage requests only.',
  },
  {
    name: 'Apify',
    purpose: 'Backend TikTok scraping for content engine research',
    data: 'No user data — public TikTok data only, used to plan content.',
  },
]

export default function Privacy() {
  return (
    <section className="parchment-bg min-h-screen pt-28 pb-20 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-deep-blue text-3xl md:text-4xl font-bold mb-2">
          Privacy Policy
        </h1>
        <p className="text-earth-brown/50 text-sm mb-10">Last Updated: May 6, 2026</p>

        <div className="bg-soft-white rounded-xl shadow-md p-8 md:p-12 space-y-8">
          <Section title="1. Introduction">
            <p>
              Illuminate Faith ("we," "our," or "us") is committed to protecting your privacy. This
              Privacy Policy explains what information we collect, how we use it, and which third
              parties help us run the service at illuminatefaithai.com.
            </p>
            <p>
              By using Illuminate, you agree to the practices described in this policy. We do not
              sell your data, and we do not train AI models on your private conversations.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>
              <strong>Account Information:</strong> When you create an account, we collect your
              email address and any profile information you provide (display name, denomination,
              maturity level, primary goal, preferred Bible translation).
            </p>
            <p>
              <strong>Chat &amp; Prayer Data:</strong> When you use AI chat, prayer generation,
              Bible AI Insight, or any other AI feature, we store your conversation history so you
              can return to it and so the AI has context for follow-up questions.
            </p>
            <p>
              <strong>Onboarding Preferences:</strong> The denomination, maturity, role, and goal
              selections you make during onboarding personalize your home dashboard, chat starters,
              and Bible reader defaults.
            </p>
            <p>
              <strong>Payment Information:</strong> If you subscribe to Premium, Stripe handles the
              payment. We never see or store your card number — we receive only a Stripe customer ID
              and subscription status.
            </p>
            <p>
              <strong>Usage &amp; Analytics:</strong> PostHog (loaded only after you accept cookies)
              captures product analytics events tied to your account so we can see where people get
              stuck in onboarding, chat, and the paywall flow. Session replay is enabled on
              marketing and onboarding pages only — never on /chat, /prayer, /bible, /ministry-map,
              or /learn.
            </p>
            <p>
              <strong>Newsletter &amp; Contact Form:</strong> If you sign up for the newsletter or
              submit the contact form, we collect your email address (and name if provided) via
              MailerLite and Resend, respectively.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use your information to:</p>
            <Bullets
              items={[
                'Provide, operate, and improve Illuminate Faith',
                'Personalize your chat, home dashboard, and Bible reader based on your stated preferences',
                'Process your subscription and manage billing',
                'Send service-related emails (account confirmations, billing receipts, password resets)',
                'Send newsletter and lifecycle emails through MailerLite (you can unsubscribe at any time)',
                'Understand product usage in aggregate so we know what to fix and what to build next',
                'Respond to support requests',
              ]}
            />
            <p>
              We do <strong>not</strong> use your private conversations to train AI models, and we
              do not sell, rent, or trade your data to third parties for advertising.
            </p>
          </Section>

          <Section title="4. Sub-Processors">
            <p>
              We share data with the following service providers who help us operate Illuminate.
              Each is bound by their own privacy policy and data-protection terms.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-[var(--gold)]/30">
                    <th className="text-left py-2 pr-3 font-semibold">Provider</th>
                    <th className="text-left py-2 pr-3 font-semibold">Purpose</th>
                    <th className="text-left py-2 font-semibold">Data Touched</th>
                  </tr>
                </thead>
                <tbody>
                  {SUB_PROCESSORS.map(p => (
                    <tr key={p.name} className="border-b border-earth-brown/10 align-top">
                      <td className="py-3 pr-3 font-semibold whitespace-nowrap">{p.name}</td>
                      <td className="py-3 pr-3">{p.purpose}</td>
                      <td className="py-3">{p.data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-earth-brown/60 mt-2">
              We may also disclose information if required by law, court order, or to protect the
              rights and safety of our users or the public.
            </p>
          </Section>

          <Section title="5. Session Replay — What Is and Is Not Recorded">
            <p>
              PostHog session replay captures mouse movement, clicks, and scroll behavior on
              marketing and onboarding pages so we can see where new visitors stumble. It is
              hard-disabled on the following routes and any nested paths:
            </p>
            <Bullets
              items={[
                '/chat — every conversation with Grace, Logos, Ezra, or Spurgeon',
                '/prayer — your generated prayers and reflections',
                '/bible — every Bible reader session, including AI Insight and original-language tools',
                '/ministry-map — your Ministry Map of people you pray for',
                '/learn — your Learn path progress and reflections',
              ]}
            />
            <p>These are the surfaces where your faith life happens. We will not record them.</p>
            <p>
              Session replay also only loads after you accept cookies. If you decline, replay never
              starts on any page.
            </p>
          </Section>

          <Section title="6. Data Storage and Security">
            <p>
              Your data is stored in Supabase's encrypted, industry-standard cloud database.
              Authentication uses Supabase Auth with bcrypt-hashed passwords. We follow reasonable
              technical and organizational measures to protect your information against unauthorized
              access, alteration, disclosure, or destruction.
            </p>
            <p>
              No method of transmission over the Internet is 100% secure. While we strive to protect
              your data, we cannot guarantee its absolute security.
            </p>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain your account information and chat history for as long as your account is
              active. If you request account deletion, we will delete your personal data within 30
              days, except where retention is required by law or legitimate business necessity (for
              example, payment records that must be kept for tax purposes).
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p>You have the right to:</p>
            <Bullets
              items={[
                'Access the personal information we hold about you',
                'Request correction of inaccurate or incomplete information',
                'Request deletion of your account and associated data',
                'Opt out of marketing emails at any time via the unsubscribe link in any email',
                'Decline analytics cookies via the consent banner (PostHog will not load)',
                'Request a copy of your data in a portable format',
              ]}
            />
            <p>
              To exercise any of these rights, contact us at{' '}
              <a
                href="mailto:support@illuminatefaithai.com"
                className="text-[var(--gold)] hover:underline"
              >
                support@illuminatefaithai.com
              </a>
              .
            </p>
          </Section>

          <Section title="9. Cookies">
            <p>We use a small set of cookies and local-storage values:</p>
            <Bullets
              items={[
                'Essential — your login session, theme preference, and onboarding state. Always on; required for the app to work.',
                'Analytics — PostHog product analytics. Loads only when you accept the cookie consent banner. Decline and it never loads.',
                'Conversion — Meta Pixel. Fires only on signup and subscribe events. No chat content.',
              ]}
            />
            <p>
              You can clear your cookie consent choice at any time by clearing your browser's local
              storage for illuminatefaithai.com — the banner will reappear on your next visit.
            </p>
          </Section>

          <Section title="10. Children's Privacy">
            <p>
              Illuminate Faith is intended for users 13 years of age and older. We do not knowingly
              collect personal information from children under 13. If we become aware that a child
              under 13 has provided us with personal information, we will delete it promptly.
            </p>
            <p>
              If you believe we may have inadvertently collected information from a child under 13,
              please contact us at{' '}
              <a
                href="mailto:support@illuminatefaithai.com"
                className="text-[var(--gold)] hover:underline"
              >
                support@illuminatefaithai.com
              </a>
              .
            </p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant
              changes by posting the updated policy on this page with a revised date. Your continued
              use of the service after changes are posted constitutes your acceptance of the updated
              policy.
            </p>
          </Section>

          <Section title="12. Contact Us">
            <p>If you have questions or concerns about this Privacy Policy or your data:</p>
            <Bullets
              items={['Email: support@illuminatefaithai.com', 'Location: Montgomery, Texas']}
            />
          </Section>
        </div>
      </div>
    </section>
  )
}
